import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface UserPreferences {
  id: string;
  userId: string;
  pushEnabled: boolean;
  pushSubscription: any | null;
  shortcutCreated: boolean;
  notificationPermission: "not_asked" | "granted" | "denied" | "dismissed";
  shortcutPermission: "not_asked" | "created" | "dismissed";
}

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchPreferences = useCallback(async () => {
    if (!user) {
      setPreferences(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error) {
        // If no preferences exist, create them
        if (error.code === "PGRST116") {
          const { data: newData, error: insertError } = await supabase
            .from("user_preferences")
            .insert({ user_id: user.id })
            .select()
            .single();

          if (insertError) throw insertError;
          
          setPreferences({
            id: newData.id,
            userId: newData.user_id,
            pushEnabled: newData.push_enabled || false,
            pushSubscription: newData.push_subscription,
            shortcutCreated: newData.shortcut_created || false,
            notificationPermission: newData.notification_permission as any || "not_asked",
            shortcutPermission: newData.shortcut_permission as any || "not_asked",
          });
        } else {
          throw error;
        }
      } else {
        // Map null values to "not_asked" for prompts to work correctly
        const notificationPerm = data.notification_permission || "not_asked";
        const shortcutPerm = data.shortcut_permission || "not_asked";
        
        setPreferences({
          id: data.id,
          userId: data.user_id,
          pushEnabled: data.push_enabled || false,
          pushSubscription: data.push_subscription,
          shortcutCreated: data.shortcut_created || false,
          notificationPermission: notificationPerm as any,
          shortcutPermission: shortcutPerm as any,
        });
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreference = async (key: keyof UserPreferences, value: any) => {
    if (!user || !preferences) return false;

    const columnMap: Record<string, string> = {
      pushEnabled: "push_enabled",
      pushSubscription: "push_subscription",
      shortcutCreated: "shortcut_created",
      notificationPermission: "notification_permission",
      shortcutPermission: "shortcut_permission",
    };

    const column = columnMap[key];
    if (!column) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .update({ [column]: value })
        .eq("user_id", user.id);

      if (error) throw error;

      setPreferences((prev) =>
        prev ? { ...prev, [key]: value } : null
      );
      return true;
    } catch (error) {
      console.error("Error updating preference:", error);
      return false;
    }
  };

  const updateShortcutPermission = async (permission: "created" | "dismissed") => {
    return updatePreference("shortcutPermission", permission);
  };

  const resetShortcutPrompt = async () => {
    return updatePreference("shortcutPermission", "not_asked");
  };

  const updateNotificationPermission = async (permission: "granted" | "denied" | "dismissed") => {
    return updatePreference("notificationPermission", permission);
  };

  const resetNotificationPrompt = async () => {
    return updatePreference("notificationPermission", "not_asked");
  };

  const updatePushSubscription = async (subscription: any) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .update({
          push_enabled: true,
          push_subscription: subscription,
          notification_permission: "granted",
        })
        .eq("user_id", user.id);

      if (error) throw error;

      setPreferences((prev) =>
        prev
          ? {
              ...prev,
              pushEnabled: true,
              pushSubscription: subscription,
              notificationPermission: "granted",
            }
          : null
      );
      return true;
    } catch (error) {
      console.error("Error updating push subscription:", error);
      return false;
    }
  };

  const disablePushNotifications = async () => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .update({
          push_enabled: false,
          push_subscription: null,
        })
        .eq("user_id", user.id);

      if (error) throw error;

      setPreferences((prev) =>
        prev
          ? {
              ...prev,
              pushEnabled: false,
              pushSubscription: null,
            }
          : null
      );
      return true;
    } catch (error) {
      console.error("Error disabling push notifications:", error);
      return false;
    }
  };

  return {
    preferences,
    loading,
    updateShortcutPermission,
    resetShortcutPrompt,
    updateNotificationPermission,
    resetNotificationPrompt,
    updatePushSubscription,
    disablePushNotifications,
    refetch: fetchPreferences,
  };
};
